import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown, Bug, Rat, Droplets, TreePine, Leaf, Wind, Sparkles, Shield, Waves, CircleDot, Gift } from "lucide-react";
import logo from "@/assets/logo_usa.webp";
import { useCity } from "@/contexts/CityContext";
import { trackEvent, trackGoogleAdsWhatsAppConversion } from "@/lib/tracking";
import { trackConversion } from "@/lib/analyticsTracker";

const services = [
  { name: "Dedetização Profissional", path: "/servicos/dedetizacao", icon: Bug },
  { name: "Desratização", path: "/servicos/desratizacao", icon: Rat },
  { name: "Limpeza de Caixa D'Água", path: "/servicos/limpeza-caixa-dagua", icon: Droplets },
  { name: "Descupinização", path: "/servicos/descupinizacao", icon: TreePine },
  { name: "Tratamento contra Formigas", path: "/servicos/formigas", icon: Leaf },
  { name: "FOG – Fumigação", path: "/servicos/fog", icon: Wind },
  { name: "Sanitização de Ambientes", path: "/servicos/sanitizacao", icon: Sparkles },
  { name: "Tratamento Anti-Traças", path: "/servicos/anti-tracas", icon: Shield },
  { name: "Limpeza de Caixa de Gordura", path: "/servicos/caixa-gordura", icon: Waves },
  { name: "Pulgas e Carrapatos", path: "/servicos/pulgas-carrapatos", icon: Bug },
  { name: "Aranhas e Escorpiões", path: "/servicos/aranhas-escorpioes", icon: CircleDot },
  { name: "Afastamento de Pombos", path: "/servicos/afastamento-pombos", icon: Wind },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { isRio, phoneDisplay, phone, whatsAppNumber, urlPrefix, email } = useCity();

  // Close mobile menu on any route change (pathname OR search params)
  useEffect(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setServicesOpen(false);
  }, [location.pathname, location.search, location.key]);

  const isActive = (path: string) => location.pathname === `${urlPrefix}${path}` || location.pathname === path;

  const whatsAppHref = `https://wa.me/${whatsAppNumber}`;
  const homeLink = isRio ? "/rio-de-janeiro" : "/";

  const handleWhatsAppClick = (source: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    trackEvent({
      event: "click_cta_primary",
      page_path: location.pathname,
      page_title: document.title,
      cta_location: source,
    });
    trackConversion("whatsapp_click", { source });
    trackGoogleAdsWhatsAppConversion(isRio);
    setTimeout(() => {
      window.open(whatsAppHref, "_blank", "noopener,noreferrer");
    }, 300);
  };

  const prefixed = (path: string) => `${urlPrefix}${path}`;

  return (
    <>
      {/* Top bar */}
      <div className="text-sm py-2" style={{ background: "#0d2a1c", color: "#f0f7f3" }}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <a href={`mailto:${email}`} aria-label={`Enviar email para ${email}`} className="flex items-center gap-1 hover:opacity-80 transition-opacity">
              <Mail className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{email}</span>
            </a>
            <a href={phone} className="flex items-center gap-1 hover:opacity-80 transition-opacity">
              <Phone className="w-3.5 h-3.5" />
              <span>{phoneDisplay}</span>
            </a>
          </div>
          <span className="hidden md:block font-medium">Resultado satisfatório ou seu dinheiro de volta!</span>
        </div>
      </div>

      {/* Main nav */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between h-16">
          <Link to={homeLink} className="flex items-center">
            <img src={logo} alt="Ultra Serviços Ambientais" className="h-12 w-auto" width={90} height={48} />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavItem to={homeLink} active={location.pathname === homeLink}>Home</NavItem>
            <NavItem to={prefixed("/diferencial")} active={isActive("/diferencial")}>Diferencial</NavItem>
            <NavItem to={prefixed("/quem-somos")} active={isActive("/quem-somos")}>Quem Somos</NavItem>

            {/* Services dropdown – pure CSS hover */}
            <div className="relative group" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <Link
                to={prefixed("/servicos")}
                className={`px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1 transition-colors hover:text-primary hover:bg-nature-light ${location.pathname.includes("/servicos") ? "text-primary bg-nature-light" : "text-foreground"}`}
              >
                Serviços <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </Link>
              <div
                className={`absolute top-full left-0 bg-card rounded-lg shadow-lg border py-2 w-72 transition-all duration-200 ${servicesOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}
              >
                {services.map((s) => (
                  <Link
                    key={s.path}
                    to={prefixed(s.path)}
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-nature-light hover:text-primary ${isActive(s.path) ? "text-primary bg-nature-light" : "text-foreground"}`}
                  >
                    <s.icon className="w-4 h-4" />
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>

            <NavItem to={prefixed("/beneficios")} active={isActive("/beneficios")}>
              <span className="flex items-center gap-1"><Gift className="w-3.5 h-3.5" />Ganhe com a USA</span>
            </NavItem>
            <NavItem to={prefixed("/blog")} active={isActive("/blog")}>Blog</NavItem>
            <NavItem to={prefixed("/contato")} active={isActive("/contato")}>Contato</NavItem>
          </nav>

          <div className="hidden lg:flex">
            <a
              href={whatsAppHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick("header_desktop")}
              className="bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Solicitar Orçamento
            </a>
          </div>

          <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu – CSS grid transition instead of framer-motion height:auto */}
        <div
          className="lg:hidden border-t bg-card overflow-hidden transition-[grid-template-rows] duration-300 ease-out grid"
          style={{ gridTemplateRows: mobileOpen ? "1fr" : "0fr" }}
        >
          <div className="min-h-0 overflow-y-auto max-h-[70vh]">
            <nav className="flex flex-col p-4 gap-1">
              <MobileNavItem to={homeLink} onClick={() => setMobileOpen(false)}>Home</MobileNavItem>
              <MobileNavItem to={prefixed("/diferencial")} onClick={() => setMobileOpen(false)}>Diferencial</MobileNavItem>
              <MobileNavItem to={prefixed("/quem-somos")} onClick={() => setMobileOpen(false)}>Quem Somos</MobileNavItem>
              <div className="flex items-center justify-between w-full">
                <MobileNavItem to={prefixed("/servicos")} onClick={() => setMobileOpen(false)}>Serviços</MobileNavItem>
                <button
                  onClick={(e) => { e.stopPropagation(); setMobileServicesOpen(!mobileServicesOpen); }}
                  className="p-2 rounded-md hover:bg-nature-light hover:text-primary transition-colors"
                  aria-label="Expandir serviços"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
              <div
                className="overflow-hidden transition-[grid-template-rows] duration-300 ease-out grid pl-3"
                style={{ gridTemplateRows: mobileServicesOpen ? "1fr" : "0fr" }}
              >
                <div className="min-h-0 flex flex-col">
                  {services.map((s) => (
                    <MobileNavItem key={s.path} to={prefixed(s.path)} onClick={() => setMobileOpen(false)}>
                      <s.icon className="w-4 h-4 inline mr-2" />
                      {s.name}
                    </MobileNavItem>
                  ))}
                </div>
              </div>
              <MobileNavItem to={prefixed("/beneficios")} onClick={() => setMobileOpen(false)}>
                <Gift className="w-4 h-4 inline mr-2" />Ganhe com a USA
              </MobileNavItem>
              <MobileNavItem to={prefixed("/blog")} onClick={() => setMobileOpen(false)}>Blog</MobileNavItem>
              <MobileNavItem to={prefixed("/contato")} onClick={() => setMobileOpen(false)}>Contato</MobileNavItem>
              <a
                href={whatsAppHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick("header_mobile")}
                className="mt-3 bg-primary text-primary-foreground text-center py-3 rounded-lg font-semibold"
              >
                Solicitar Orçamento
              </a>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

const NavItem = ({ to, active, children }: { to: string; active: boolean; children: React.ReactNode }) => (
  <Link
    to={to}
    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors hover:text-primary hover:bg-nature-light ${active ? "text-primary bg-nature-light" : "text-foreground"}`}
  >
    {children}
  </Link>
);

const MobileNavItem = ({ to, onClick, children }: { to: string; onClick: () => void; children: React.ReactNode }) => (
  <Link to={to} onClick={onClick} className="px-3 py-2.5 rounded-md text-sm hover:bg-nature-light hover:text-primary transition-colors">
    {children}
  </Link>
);

export default Header;
